import React, { useState } from "react";
import styles from "./SetupAccount.module.css";
import NextButton from "../../components/common/NextButton";
import InlineSpinner from "../../components/common/InlineSpinner";
import { useOnboarding } from "../../hooks/useOnboarding";
import { useAuth } from "../../contexts/AuthContext";
import onboardingService from "../../services/onboardingService";

interface SetupAccountProps {
  onNext: (data?: any) => void;
}

// URL validation pattern
const urlPattern = new RegExp(
  "^(https?://)?([\\w.-]+\\.[A-Za-z]{2,})(/[\\w\\-._~:/?#[\\]@!$&'()*+,;=]*)?$"
);

const SetupAccount: React.FC<SetupAccountProps> = ({ onNext }) => {
  const { user } = useAuth();
  const { data, setAccountInfo } = useOnboarding();
  const [firstName, setFirstName] = useState(data.firstName || "");
  const [lastName, setLastName] = useState(data.lastName || "");
  const [companyName, setCompanyName] = useState(data.companyName || "");
  const [companyWebsite, setCompanyWebsite] = useState(
    data.companyWebsite || ""
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Form is valid only when all fields are non-empty and URL matches pattern
  const isValid =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    companyName.trim() !== "" &&
    urlPattern.test(companyWebsite.trim());

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !user || !user.email) {
      setError("Please fill in all required fields correctly.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    // Normalize URL: prepend https:// if no protocol is present
    const normalizedWebsite = companyWebsite.trim().match(/^https?:\/\//)
      ? companyWebsite.trim()
      : `https://${companyWebsite.trim()}`;

    try {
      // update context
      setAccountInfo({ firstName, lastName, companyName, companyWebsite: normalizedWebsite });

      // Save account info (updates users.full_name and email)
      await onboardingService.saveAccountInfo(user.id, {
        firstName,
        lastName,
        email: user.email,
      });

      // Create/update company record in database
      await onboardingService.saveCompanyInfo(
        user.id,
        companyName,
        normalizedWebsite
      );

      // Save onboarding data to Supabase users table
      const currentOnboardingData = {
        firstName,
        lastName,
        companyName,
        companyWebsite: normalizedWebsite,
        step: 1,
        completed_steps: ["account_setup"],
      };

      await onboardingService.updateOnboarding(user.id, currentOnboardingData);

      // Pass the data to onNext for validation and progression
      onNext({ firstName, lastName, companyName, companyWebsite: normalizedWebsite });
    } catch (error) {
      console.error("Failed to save account setup:", error);
      setError("Failed to save your information. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.main}>
          <div className={styles.formSection}>
            <h1 className={styles.heading}>Tell us more about you.</h1>
            <p className={styles.subheading}>
              This information will help us to give you a better experience.
            </p>

            {error && <div className={styles.errorBox}>{error}</div>}

            <form onSubmit={handleNext}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="firstName" className={styles.label}>
                    First Name*
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    className={styles.input}
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="lastName" className={styles.label}>
                    Last Name*
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    className={styles.input}
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="companyName" className={styles.label}>
                    Company Name*
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    className={styles.input}
                    placeholder="Enter your company name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="companyWebsite" className={styles.label}>
                    Company Website*
                  </label>
                  <input
                    id="companyWebsite"
                    type="text"
                    className={styles.input}
                    placeholder="Enter your company website"
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    required
                  />
                </div>
              </div>
              <NextButton
                type="submit"
                disabled={!isValid || isSubmitting}
                className={styles.submitButton}
              >
                {isSubmitting ? <InlineSpinner /> : "Next"}
              </NextButton>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupAccount;
